import React, { useEffect, useMemo, useState } from "react";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { db, auth } from "../firebase";
import { Timestamp, doc, getDoc, onSnapshot, updateDoc } from "firebase/firestore";
import "../styles/Modal.css";
import "../styles/Factures.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FirestoreDevis } from "../types/firestore";
import { CSVLink } from "react-csv";
import { FaEdit, FaTrash, FaFileExport } from "react-icons/fa";
import { IconType } from "react-icons";
import FactureModal from "../components/FactureModal";
import { useNavigate, useLocation } from "react-router-dom";
import FacturePreviewModal from '../components/FacturePreviewModal';
import BillingRecapTable from '../components/BillingRecapTable';
import { usePageTitle } from "../hooks/usePageTitle";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { getPDFCoordinatesFromStorage } from "../utils/pdfCoordinates";
import { PDF_SCALE, PDF_FONT_SIZE_OFFSET, Y_OFFSET_CORRECTION } from "../constants/pdfConfig";
import { mmToPixels } from "../utils/units";
import { logUserAction } from "../utils/logUtils";
import { useDemoContext } from "../context/DemoContext";
import {
  FACTURE_PDF_SCALE,
  FACTURE_FONT_SIZE_OFFSET,
  FACTURE_Y_OFFSET_CORRECTION,
  FACTURE_Y_OFFSET_PDF_GENERATION,
  FACTURE_X_OFFSET_PDF_GENERATION
} from "../constants/FactureConfig";

const FaEditIcon: IconType = FaEdit;
const FaTrashIcon: IconType = FaTrash;
const FaFileExportIcon: IconType = FaFileExport;

interface Invoice {
  id: string;
  companyId: string;
  opportunityType: string;
  qte: number;
  pu: number;
  montantHT: number;
  montantTTC: number;
  date: Timestamp;
  updatedAt: Timestamp;
  uid?: string;
  opportunityDate?: string;
  status?: string;
  pourcentage?: number;
  montantFactureHT?: number;
  montantFactureTTC?: number;
}

interface CompanyData {
  name?: string;
  siret?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  principalGender?: string;
  principalFirstName?: string;
  principalContact?: string;
  principalPhone?: string;
  principalEmail?: string;
  principalPosition?: string;
  [key: string]: any;
}

type FieldSource = 'Informations Facture' | 'Informations Entreprise' | 'Contact Principal';

const Factures: React.FC = () => {
  const location = useLocation();
  const { isDemoMode, companiesDocId } = useDemoContext();
  // États
  const [invoicesList, setInvoicesList] = useState<Invoice[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [invoiceToEdit, setInvoiceToEdit] = useState<Invoice | null>(null);
  const [preselectedDevis, setPreselectedDevis] = useState<FirestoreDevis | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth().toString());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedOpportunityType, setSelectedOpportunityType] = useState("all");

  // États pour la modale d'aperçu PDF
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [invoiceToPreview, setInvoiceToPreview] = useState<Invoice | null>(null);
  const [invoicePdfPreviewUrl, setInvoicePdfPreviewUrl] = useState<string | null>(null);

  // New state for confirmation modal
  const [invoiceToDelete, setInvoiceToDelete] = useState<Invoice | null>(null);

  // États pour le tri
  const [sortField, setSortField] = useState<string>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // État pour les opportunités par société
  const [companiesOpportunities, setCompaniesOpportunities] = useState<{[key: string]: any[]}>({});

  // Titre de la page
  usePageTitle("Mes factures");

  const filteredInvoices = useMemo(() => {
    const lower = searchTerm.toLowerCase();
    const result = invoicesList.filter(
      (invoice) => {
        const matchesSearch = invoice.companyId.toLowerCase().includes(lower) ||
          invoice.opportunityType.toLowerCase().includes(lower) ||
          invoice.id.toLowerCase().includes(lower);

        // Statut normalisé ("Pas Réglée" et vide = "En attente de Paiement")
        const effectiveStatus = invoice.status === 'Réglée' || invoice.status === 'Gelée'
          ? invoice.status
          : 'En attente de Paiement';
        const matchesStatus = selectedStatus === "all" || effectiveStatus === selectedStatus;
        const matchesOpportunityType = selectedOpportunityType === "all" || invoice.opportunityType === selectedOpportunityType;

        // Vérifier que la date existe avant de l'utiliser
        if (!invoice.date) {
          // Si pas de date, ne pas filtrer par mois/année
          return matchesSearch && matchesStatus && matchesOpportunityType;
        }

        const invoiceDate = invoice.date.toDate();
        const matchesMonth = selectedMonth === "all" || invoiceDate.getMonth() === parseInt(selectedMonth);
        const matchesYear = selectedYear === "all" || invoiceDate.getFullYear() === parseInt(selectedYear);

        return matchesSearch && matchesMonth && matchesYear && matchesStatus && matchesOpportunityType;
      }
    );
    // Apply sorting
    result.sort((a, b) => {
      const dir = sortDirection === 'asc' ? 1 : -1;
      switch (sortField) {
        case 'date': {
          const da = a.date?.toMillis?.() ?? 0;
          const db = b.date?.toMillis?.() ?? 0;
          return (da - db) * dir;
        }
        case 'id': return ((a.id || '').localeCompare(b.id || '')) * dir;
        case 'companyId': return ((a.companyId || '').localeCompare(b.companyId || '')) * dir;
        case 'opportunityType': return ((a.opportunityType || '').localeCompare(b.opportunityType || '')) * dir;
        case 'qte': return ((a.qte ?? 0) - (b.qte ?? 0)) * dir;
        case 'pu': return ((a.pu ?? 0) - (b.pu ?? 0)) * dir;
        case 'montantHT': return ((a.montantHT ?? 0) - (b.montantHT ?? 0)) * dir;
        case 'montantTTC': return ((a.montantTTC ?? 0) - (b.montantTTC ?? 0)) * dir;
        case 'pourcentage': return ((a.pourcentage ?? 0) - (b.pourcentage ?? 0)) * dir;
        case 'montantFactureHT': return ((a.montantFactureHT ?? 0) - (b.montantFactureHT ?? 0)) * dir;
        case 'montantFactureTTC': return ((a.montantFactureTTC ?? 0) - (b.montantFactureTTC ?? 0)) * dir;
        case 'status': return ((a.status || '').localeCompare(b.status || '')) * dir;
        default: return 0;
      }
    });
    return result;
  }, [invoicesList, searchTerm, selectedMonth, selectedYear, selectedStatus, selectedOpportunityType, sortField, sortDirection]);

  // Liste des types de demande distincts (pour le filtre)
  const opportunityTypes = useMemo(() => {
    const set = new Set<string>();
    invoicesList.forEach((inv) => {
      if (inv.opportunityType) set.add(inv.opportunityType);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [invoicesList]);

  const currentInvoices = filteredInvoices;

  // Calculer les totaux des factures affichées (montants réellement facturés)
  const totals = currentInvoices.reduce((acc, invoice) => {
    acc.totalFactureHT += Number(invoice.montantFactureHT) || 0;
    acc.totalFactureTTC += Number(invoice.montantFactureTTC) || 0;
    return acc;
  }, { totalFactureHT: 0, totalFactureTTC: 0 });

  // Calculer les totaux par statut (montants facturés)
  const totalsByStatus = currentInvoices.reduce((acc, invoice) => {
    const status = invoice.status || 'En attente de Paiement';

    if (status === 'Gelée') {
      // Les factures gelées ne sont comptées ni dans les réglées ni dans les en attente
      acc.gelee.totalHT += Number(invoice.montantFactureHT) || 0;
      acc.gelee.totalTTC += Number(invoice.montantFactureTTC) || 0;
    } else if (status === 'Réglée') {
      acc.reglee.totalHT += Number(invoice.montantFactureHT) || 0;
      acc.reglee.totalTTC += Number(invoice.montantFactureTTC) || 0;
    } else {
      acc.enAttente.totalHT += Number(invoice.montantFactureHT) || 0;
      acc.enAttente.totalTTC += Number(invoice.montantFactureTTC) || 0;
    }
    return acc;
  }, {
    reglee: { totalHT: 0, totalTTC: 0 },
    enAttente: { totalHT: 0, totalTTC: 0 },
    gelee: { totalHT: 0, totalTTC: 0 }
  });

  // Chargement des factures
  useEffect(() => {
    const docRef = doc(db, "prospects", companiesDocId);
    const unsubscribe = onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        const allInvoices: Invoice[] = [];
        
        // Parcourir toutes les sociétés pour récupérer leurs factures
        Object.entries(data).forEach(([companyId, companyData]: [string, any]) => {
          // Exclure les factures de ZakariaELORCHE (elles sont gérées dans FactureZak.tsx)
          if (companyId !== 'ZakariaELORCHE' && companyData.invoices) {
            companyData.invoices.forEach((invoice: Invoice) => {
              allInvoices.push({
                ...invoice,
                companyId
              });
            });
          }
        });

        // Extraire les opportunités par société
        const oppsMap: {[key: string]: any[]} = {};
        Object.entries(data).forEach(([companyId, companyData]: [string, any]) => {
          if (companyData.opportunities) {
            oppsMap[companyId] = companyData.opportunities;
          }
        });
        setCompaniesOpportunities(oppsMap);

        // Trier les factures par date (plus récentes en premier)
        allInvoices.sort((a, b) => {
          // Vérifier que les dates existent avant de les comparer
          if (!a.date || !b.date) {
            // Si une des dates est manquante, mettre la facture sans date à la fin
            if (!a.date && !b.date) return 0;
            if (!a.date) return 1;
            if (!b.date) return -1;
          }

          // Convertir les dates en millisecondes pour la comparaison
          const dateA = a.date?.toMillis?.() || 0;
          const dateB = b.date?.toMillis?.() || 0;

          return dateB - dateA; // Tri décroissant (plus récent en premier)
        });
        setInvoicesList(allInvoices);
      }
    });

    return () => unsubscribe();
  }, [companiesDocId]);

  // Détecter le devis pré-sélectionné depuis la navigation
  useEffect(() => {
    const state = location.state as { preselectedDevis?: FirestoreDevis } | null;
    if (state?.preselectedDevis) {
      setPreselectedDevis(state.preselectedDevis);
      setShowModal(true);
      // Nettoyer le state pour éviter de rouvrir le modal à chaque re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Gestion des factures
  const handleAddInvoice = () => {
    setInvoiceToEdit(null);
    setPreselectedDevis(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setInvoiceToEdit(null);
    setPreselectedDevis(null);
  };

  const handleEditInvoice = (invoice: Invoice) => {
    console.log("=== DONNÉES DE LA FACTURE À ÉDITER ===");
    console.log("Facture complète:", invoice);
    console.log("Type d'opportunité:", invoice.opportunityType);
    console.log("Quantité:", invoice.qte);
    console.log("Prix unitaire:", invoice.pu);
    console.log("Montant HT:", invoice.montantHT);
    console.log("Montant TTC:", invoice.montantTTC);
    console.log("Société:", invoice.companyId);
    
    setInvoiceToEdit(invoice);
    setShowModal(true);
  };

  const handleDeleteInvoice = (invoice: Invoice) => {
    setInvoiceToDelete(invoice); // Set the invoice to be deleted, opening the confirmation modal
  };

  const confirmDeleteInvoice = async () => {
    if (!invoiceToDelete) return;
    try {
      const docRef = doc(db, "prospects", companiesDocId);
      const companyIdToDeleteFrom = invoiceToDelete.companyId;
      if (!companyIdToDeleteFrom) {
        toast.error("Erreur lors de la suppression de la facture: Société non trouvée.");
        return;
      }
      // Fetch the latest document data to ensure we have the most up-to-date invoice list
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        toast.error("Document Firestore 'companies' non trouvé.");
        return;
      }
      const data = docSnap.data();
      // Filter out the deleted invoice from the specific company's invoices
      const updatedInvoicesForCompany = data[companyIdToDeleteFrom]?.invoices
        .filter((inv: any) => {
          if (invoiceToDelete.uid && inv.uid) {
            return inv.uid !== invoiceToDelete.uid;
          }
          // fallback : id + date
          return !(inv.id === invoiceToDelete.id && inv.date?.toMillis() === invoiceToDelete.date?.toMillis());
        }) || [];
      await updateDoc(docRef, {
        [`${companyIdToDeleteFrom}.invoices`]: updatedInvoicesForCompany
      });
      
      // Log de la suppression
      const currentUser = auth.currentUser;
      if (currentUser?.email) {
        await logUserAction(
          currentUser.email,
          "Suppression facture",
          `Facture supprimée: ${invoiceToDelete.id} pour ${companyIdToDeleteFrom} - Montant: ${invoiceToDelete.montantHT}€`
        );
      }
      
      toast.success("Facture supprimée avec succès");
    } catch (error) {
      console.error("Erreur lors de la suppression de la facture:", error);
      toast.error("Erreur lors de la suppression de la facture");
    } finally {
      setInvoiceToDelete(null); // Close the confirmation modal
    }
  };

  const cancelDeleteInvoice = () => {
    setInvoiceToDelete(null); // Close the confirmation modal without deleting
  };

  // Handler pour dupliquer une facture
  const handleDuplicateInvoice = async (invoice: Invoice) => {
    try {
      const docRef = doc(db, "prospects", companiesDocId);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        toast.error("Document non trouvé");
        return;
      }
      const data = docSnap.data();
      const companyData = data[invoice.companyId];
      if (!companyData || !companyData.invoices) {
        toast.error("Données de la société non trouvées");
        return;
      }
      // Trouver l'index de la facture à dupliquer
      const index = companyData.invoices.findIndex((inv: any) => (invoice.uid && inv.uid) ? inv.uid === invoice.uid : (inv.id === invoice.id && inv.opportunityType === invoice.opportunityType && inv.date.toMillis() === invoice.date.toMillis()));
      if (index === -1) {
        toast.error("Facture à dupliquer non trouvée");
        return;
      }
      // Générer le prochain numéro de facture automatiquement
      const allInvoices = invoicesList.concat(companyData.invoices);
      const maxId = allInvoices.reduce((max, inv) => {
        const num = parseInt(inv.id.replace(/\D/g, ''));
        return !isNaN(num) && num > max ? num : max;
      }, 0);
      const nextId = 'F' + String(maxId + 1).padStart(4, '0');
      // Créer une nouvelle facture avec un nouvel uid unique et un nouveau numéro
      const newInvoice = {
        ...invoice,
        id: nextId,
        uid: Date.now().toString() + Math.random().toString(36).substring(2, 8),
        date: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      // Insérer la copie juste après l'original
      const updatedInvoices = [...companyData.invoices];
      updatedInvoices.splice(index + 1, 0, newInvoice);
      await updateDoc(docRef, {
        [`${invoice.companyId}.invoices`]: updatedInvoices
      });
      
      // Log de la duplication
      const currentUser = auth.currentUser;
      if (currentUser?.email) {
        await logUserAction(
          currentUser.email,
          "Duplication facture",
          `Facture dupliquée: ${invoice.id} → ${nextId} pour ${invoice.companyId} - Montant: ${invoice.montantHT}€`
        );
      }
      
      toast.success("Facture dupliquée avec succès");
    } catch (error) {
      console.error("Erreur lors de la duplication de la facture:", error);
      toast.error("Erreur lors de la duplication de la facture");
    }
  };

  // Utilitaires PDF
  const rgbFromHex = (hex: string) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    return rgb(r, g, b);
  };

  const formatFieldValue = (value: any, fieldName?: string): string => {
    if (value instanceof Timestamp) {
      return value.toDate().toLocaleDateString("fr-FR");
    }
    if (value && typeof value.toDate === 'function') {
      return value.toDate().toLocaleDateString("fr-FR");
    }
    if (fieldName === 'amount' && typeof value === 'number') {
      return value.toFixed(2);
    }
    return value?.toString() || "";
  };

  const handleGenerateInvoicePdf = async (invoice: Invoice) => {
    // Si la facture possède un PDF joint, on affiche ce PDF
    if ((invoice as any).fileUrl) {
      setInvoicePdfPreviewUrl((invoice as any).fileUrl);
      setInvoiceToPreview(invoice);
      setShowPreviewModal(true);
      toast.success("Aperçu du PDF joint ouvert.");
      return;
    }

    // Sinon, comportement actuel : génération du PDF
    try {
      // 1. Récupérer les coordonnées depuis Coordinates/maFacture
      const coords = await getPDFCoordinatesFromStorage("maFacture");
      console.log("Coordonnées Facture chargées:", coords);

      if (!coords) {
        toast.error("Coordonnées PDF Facture non trouvées.");
        return;
      }

      // 2. Récupérer les infos complètes de l'entreprise
      const companyDocRef = doc(db, "prospects", companiesDocId);
      const companyDocSnap = await getDoc(companyDocRef);
      let companyData: CompanyData | undefined;

      if (companyDocSnap.exists()) {
        const allCompaniesData = companyDocSnap.data();
        companyData = allCompaniesData[invoice.companyId] as CompanyData;
        console.log("Données entreprise chargées:", companyData);
      } else {
        toast.error("Données entreprises non trouvées dans Firestore.");
        return;
      }
      
      if (!companyData) {
         toast.error(`Données spécifiques pour l'entreprise ${invoice.companyId} non trouvées.`);
         return;
      }

      // 3. Chargement du template PDF de la facture
      const pdfPath = "/pdfs/facture.pdf";
      const existingPdfBytes = await fetch(pdfPath).then((res) => {
        if (!res.ok) throw new Error(`Échec du chargement du modèle PDF Facture: ${res.status}`);
        return res.arrayBuffer();
      });
      
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const pages = pdfDoc.getPages();
      if (pages.length === 0) throw new Error("Le modèle PDF Facture n'a aucune page");

      const fontRegular = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const fontItalic = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);
      const fontBoldItalic = await pdfDoc.embedFont(StandardFonts.HelveticaBoldOblique);

      // 4. Remplissage des champs sur le PDF
      for (const [pageNumber, pageCoords] of Object.entries(coords)) {
        const pageIndex = parseInt(pageNumber) - 1;
        if (pageIndex < 0 || pageIndex >= pages.length) continue;

        const page = pages[pageIndex];
        const { height } = page.getSize();
        
         // Triez les champs par coordonnée Y pour un dessin cohérent (de haut en bas) si nécessaire
         const sortedFields = Object.entries(pageCoords).sort(([, a], [, b]) => {
           return a.y - b.y;
         });

        for (const [fullFieldName, fieldConfig] of sortedFields) {
          const { x, y, size = 12, color = "#000000", bold = false, italic = false } = fieldConfig as any;
          
          // Extraction du nom de champ et de la source
          const parts = fullFieldName.split('-');
          const source: FieldSource = parts[0] as FieldSource;
          const fieldName = parts.slice(1).join('-');

          let value = '';
          // Logique pour extraire les valeurs correctement de l'objet 'invoice' et 'companyData' en fonction de 'source' et 'fieldName'
          switch (source) {
            case 'Informations Facture':
               // Mapper les noms de champs de coordonnées aux propriétés de l'objet invoice
               switch (fieldName) {
                 case 'factureNumber': // Assumons que cela correspond à l'ID de la facture
                 case 'id':
                   value = formatFieldValue(invoice.id, fieldName);
                   break;
                 case 'factureDate': // Assumons que cela correspond à la date de la facture
                 case 'date':
                   value = formatFieldValue(invoice.date, fieldName);
                   break;
                 case 'totalHT': // Mappage pour le désalignement identifié
                 case 'montantHT':
                   value = formatFieldValue(invoice.montantHT, 'currency');
                   break;
                 case 'totalTTC': // Mappage pour le désalignement identifié
                 case 'montantTTC':
                   value = formatFieldValue(invoice.montantTTC, 'currency');
                   break;
                 case 'qte':
                   value = formatFieldValue(invoice.qte, fieldName);
                   break;
                 case 'pu': // Prix unitaire
                   value = formatFieldValue(invoice.pu, 'currency');
                   break;
                 case 'opportunityType':
                   value = formatFieldValue(invoice.opportunityType, fieldName);
                   break;
                 case 'updatedAt':
                   value = formatFieldValue(invoice.updatedAt, fieldName);
                   break;
                 // Ajoutez d'autres champs de facture ici si nécessaire
                 default:
                   console.warn(`Champ de facture inconnu ou non mappé: Informations Facture-${fieldName}`);
                   // Tentez un accès direct au cas où
                    if (fieldName in invoice) {
                      value = formatFieldValue((invoice as any)[fieldName], fieldName);
                   } else {
                     console.warn(`Champ de facture inconnu dans l'objet invoice: ${fieldName}`);
                   }
                   break;
               }
               break;
            case 'Informations Entreprise':
               // Accéder aux propriétés de l'objet 'companyData' (assumant que fieldName correspond à la propriété)
               if (companyData && fieldName in companyData) {
                  value = formatFieldValue((companyData as any)[fieldName], fieldName);
               } else {
                  console.warn(`Champ de société inconnu dans les données de l'entreprise: Informations Entreprise-${fieldName}`);
               }
               break;
             case 'Contact Principal':
               // Accéder aux propriétés du contact principal (assumant que fieldName correspond à la propriété)
                if (companyData && fieldName in companyData) {
                     value = formatFieldValue((companyData as any)[fieldName], fieldName); // Les champs contact sont aussi des propriétés de companyData
                } // TODO: Gérer d'autres contacts si les coordonnées les incluent
                else {
                    console.warn(`Champ de contact principal inconnu dans les données de l'entreprise: Contact Principal-${fieldName}`);
                }
               break;
            default:
              // Source inconnue ou non gérée (ne devrait plus arriver avec la correction précédente du type FieldSource)
              console.warn(`Source de champ inconnue: ${source}`);
              break;
          }

          if (value) {
            // Nouveau calcul X/Y avec FactureConfig
            const xPdf = (x * FACTURE_PDF_SCALE) + FACTURE_X_OFFSET_PDF_GENERATION;
            const yPdf = (y * FACTURE_PDF_SCALE) + FACTURE_Y_OFFSET_PDF_GENERATION;
            const adjustedYForPdfLib = height - yPdf;
            const finalY = adjustedYForPdfLib - (size * (FACTURE_FONT_SIZE_OFFSET || 0)) + (FACTURE_Y_OFFSET_CORRECTION || 0);

            let font = fontRegular;
            if (bold && italic) font = fontBoldItalic;
            else if (bold) font = fontBold;
            else if (italic) font = fontItalic;
            
            const textColor = color ? rgbFromHex(color) : rgb(0, 0, 0);

            page.drawText(value, {
              x: xPdf,
              y: finalY,
              size: size,
              font,
              color: textColor,
            });
          }
        }
      }

      // 5. Génération du PDF final
      const pdfBytes = await pdfDoc.save();
      // Conversion explicite pour garantir la compatibilité avec BlobPart
      const uint8Array = new Uint8Array(pdfBytes.length);
      uint8Array.set(pdfBytes);
      const blob = new Blob([uint8Array], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);

      // Stocker l'URL et ouvrir la modale
      setInvoicePdfPreviewUrl(blobUrl);
      setInvoiceToPreview(invoice);
      setShowPreviewModal(true);
      toast.success("PDF Facture généré avec succès !");

    } catch (error) {
      console.error("Erreur génération PDF Facture:", error);
      toast.error(`Échec génération PDF Facture: ${error instanceof Error ? error.message : String(error)}`);
      setInvoicePdfPreviewUrl(null);
      setInvoiceToPreview(null);
      setShowPreviewModal(false);
    }
  };

  const handleExportCSV = () => {
    const headers = [
      "ID",
      "Société",
      "Type de demande",
      "Quantité",
      "Prix unitaire",
      "Montant HT",
      "Montant TTC",
      "Date de création"
    ];

    const csvData = invoicesList.map(invoice => [
      invoice.id,
      invoice.companyId,
      invoice.opportunityType,
      invoice.qte,
      invoice.pu,
      invoice.montantHT,
      invoice.montantTTC,
      invoice.date ? invoice.date.toDate().toLocaleDateString() : "-"
    ]);

    const csvContent = [
      headers.join(","),
      ...csvData.map(row => row.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `factures_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatValue = (value: any, type: string) => {
    if (value === undefined || value === null) return "-";
    if (type === "date") {
      // Vérifier que la valeur a une méthode toDate
      if (value && typeof value.toDate === 'function') {
        return value.toDate().toLocaleDateString();
      }
      // Si c'est déjà une Date
      if (value instanceof Date) {
        return value.toLocaleDateString();
      }
      return "-";
    }
    if (type === "currency") {
      return `${value.toLocaleString("fr-FR")} €`;
    }
    return value.toString();
  };

  const months = [
    { value: "all", label: "Afficher Tout" },
    { value: "0", label: "Janvier" },
    { value: "1", label: "Février" },
    { value: "2", label: "Mars" },
    { value: "3", label: "Avril" },
    { value: "4", label: "Mai" },
    { value: "5", label: "Juin" },
    { value: "6", label: "Juillet" },
    { value: "7", label: "Août" },
    { value: "8", label: "Septembre" },
    { value: "9", label: "Octobre" },
    { value: "10", label: "Novembre" },
    { value: "11", label: "Décembre" }
  ];

  const currentYear = new Date().getFullYear();
  const years = [
    { value: "all", label: "Afficher Tout" },
    ...Array.from({ length: 5 }, (_, i) => ({
      value: (currentYear - i).toString(),
      label: (currentYear - i).toString()
    }))
  ];

  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false);
    setInvoiceToPreview(null);
    if (invoicePdfPreviewUrl) {
       URL.revokeObjectURL(invoicePdfPreviewUrl);
       setInvoicePdfPreviewUrl(null);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'date' ? 'desc' : 'asc');
    }
  };

  const renderSortIcon = (field: string) => {
    if (sortField !== field) return <i className="bi bi-arrow-down-up" style={{ color: '#6c757d', opacity: 0.4, fontSize: '0.75rem', marginLeft: '4px' }} />;
    return sortDirection === 'asc'
      ? <i className="bi bi-arrow-up" style={{ color: '#007bff', fontSize: '0.75rem', marginLeft: '4px' }} />
      : <i className="bi bi-arrow-down" style={{ color: '#007bff', fontSize: '0.75rem', marginLeft: '4px' }} />;
  };

  return (
    <div className="factures-container" style={isDemoMode ? { paddingTop: '120px' } : undefined}>
      <ToastContainer position="top-right" autoClose={2000} />
      <Header 
        isAuthenticated={true} 
        onLoginClick={() => navigate('/login')} 
        onHomeClick={() => navigate('/')} 
        onLogoutClick={handleLogout}
      />
      <h2 className="factures-title" style={{ color: '#424272' }}>Liste des Factures</h2>

      <div className="factures-actions-bar">
        <button
          className="add-opportunite-button"
          onClick={handleAddInvoice}
          style={{ fontFamily: 'Inter, sans-serif', backgroundColor: '#424272' }}
        >
          + Ajouter une facture
        </button>
        <button 
          className="add-opportunite-button" 
          onClick={handleExportCSV} 
          style={{ fontFamily: 'Inter, sans-serif', backgroundColor: '#424272' }}
        >
          Exporter en CSV
        </button>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <button type="button" title="Mois précédent" onClick={() => { if (selectedMonth === 'all') return; const m = parseInt(selectedMonth); setSelectedMonth(String(m === 0 ? 11 : m - 1)); }} style={{ cursor: 'pointer', border: '1px solid #ced4da', background: '#fff', borderRadius: '4px', padding: '4px 8px', color: '#424272', fontWeight: 700, lineHeight: 1 }}>
            <i className="bi bi-chevron-left"></i>
          </button>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="opportunites-search-bar"
            style={{ fontFamily: 'Inter, sans-serif', maxWidth: "130px" }}
          >
            {months.map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
          <button type="button" title="Mois suivant" onClick={() => { if (selectedMonth === 'all') return; const m = parseInt(selectedMonth); setSelectedMonth(String(m === 11 ? 0 : m + 1)); }} style={{ cursor: 'pointer', border: '1px solid #ced4da', background: '#fff', borderRadius: '4px', padding: '4px 8px', color: '#424272', fontWeight: 700, lineHeight: 1 }}>
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
          className="opportunites-search-bar"
          style={{ fontFamily: 'Inter, sans-serif', maxWidth: "120px" }}
        >
          {years.map((year) => (
            <option key={year.value} value={year.value}>
              {year.label}
            </option>
          ))}
        </select>
        <select
          value={selectedOpportunityType}
          onChange={(e) => setSelectedOpportunityType(e.target.value)}
          className="opportunites-search-bar"
          style={{ fontFamily: 'Inter, sans-serif', maxWidth: "200px" }}
        >
          <option value="all">Tous les types de demande</option>
          {opportunityTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="opportunites-search-bar"
          style={{ fontFamily: 'Inter, sans-serif', maxWidth: "180px" }}
        >
          <option value="all">Tous les statuts</option>
          <option value="En attente de Paiement">En attente de Paiement</option>
          <option value="Réglée">Réglée</option>
          <option value="Gelée">Gelée</option>
        </select>
        <input
          type="text"
          className="opportunites-search-bar"
          placeholder="Rechercher une facture..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ fontFamily: 'Inter, sans-serif' }}
        />
      </div>

      <div className="factures-table-container">
        <table className="factures-table" style={{ fontFamily: 'Inter, sans-serif' }}>
          <thead>
            <tr>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort('id')}
              >
                Numéro{renderSortIcon('id')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort('companyId')}
              >
                Société{renderSortIcon('companyId')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort('opportunityType')}
              >
                Type de demande{renderSortIcon('opportunityType')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort('qte')}
              >
                Qté{renderSortIcon('qte')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort('pu')}
              >
                PU (€){renderSortIcon('pu')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort('montantHT')}
              >
                Opp.HT(€){renderSortIcon('montantHT')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort('montantTTC')}
              >
                Oppt.TTC(€){renderSortIcon('montantTTC')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort('pourcentage')}
              >
                %{renderSortIcon('pourcentage')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort('montantFactureHT')}
              >
                Facturé (HT){renderSortIcon('montantFactureHT')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort('montantFactureTTC')}
              >
                Facturé (TTC){renderSortIcon('montantFactureTTC')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort('date')}
              >
                Date de création{renderSortIcon('date')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort('status')}
              >
                Statut{renderSortIcon('status')}
              </th>
              <th style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>Documents</th>
              <th style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentInvoices.length === 0 ? (
              <tr>
                <td colSpan={13} style={{ textAlign: "center", fontFamily: 'Inter, sans-serif' }}>Aucune facture trouvée.</td>
              </tr>
            ) : (
              <>
                {currentInvoices.map((invoice, index) => (
                  <tr key={invoice.uid || (invoice.id + '-' + index)}>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{invoice.id}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{invoice.companyId}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>
                      {(() => {
                        const opps = companiesOpportunities[invoice.companyId] || [];
                        const matchingOpp =
                          (invoice.opportunityDate && opps.find((opp: any) => opp.type === invoice.opportunityType && String(opp.date) === String(invoice.opportunityDate))) ||
                          opps.find((opp: any) => opp.type === invoice.opportunityType && Number(opp.qte) === Number(invoice.qte) && Math.abs(Number(opp.pu) - Number(invoice.pu)) < 0.01) ||
                          opps.find((opp: any) => opp.type === invoice.opportunityType && Math.abs(Number(opp.amount) - Number(invoice.montantHT)) < 0.01) ||
                          opps.find((opp: any) => opp.type === invoice.opportunityType) || null;
                        return (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            {invoice.opportunityType}
                            <Tippy
                              content={
                                <div style={{ fontSize: '0.82rem', textAlign: 'left', padding: '2px 4px', lineHeight: '1.6' }}>
                                  {matchingOpp ? (
                                    <>
                                      <div><strong>Contact :</strong> {matchingOpp.contact || '-'}</div>
                                      <div><strong>Qté :</strong> {matchingOpp.qte ?? '-'}</div>
                                      <div><strong>PU :</strong> {matchingOpp.pu ? `${Number(matchingOpp.pu).toFixed(2)} €` : '-'}</div>
                                      <div><strong>Remise :</strong> {matchingOpp.remise !== undefined ? `${matchingOpp.remise} %` : '-'}</div>
                                      <div><strong>Montant HT :</strong> {matchingOpp.amount ? `${Number(matchingOpp.amount).toFixed(2)} €` : '-'}</div>
                                      <div><strong>Montant TTC :</strong> {matchingOpp.ttc ? `${Number(matchingOpp.ttc).toFixed(2)} €` : '-'}</div>
                                      <div><strong>Date :</strong> {matchingOpp.date ? new Date(matchingOpp.date).toLocaleDateString('fr-FR') : '-'}</div>
                                      <div><strong>Statut :</strong> {matchingOpp.status || '-'}</div>
                                      {matchingOpp.description && <div><strong>Description :</strong> {matchingOpp.description}</div>}
                                    </>
                                  ) : (
                                    <div>Aucune opportunité associée</div>
                                  )}
                                </div>
                              }
                              placement="right"
                              allowHTML={false}
                              interactive={true}
                            >
                              <i
                                className="bi bi-info-circle"
                                style={{ cursor: 'help', color: matchingOpp ? '#007bff' : '#adb5bd', fontSize: '0.9rem' }}
                              />
                            </Tippy>
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{invoice.qte}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{formatValue(invoice.pu, "currency")}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{formatValue(invoice.montantHT, "currency")}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{formatValue(invoice.montantTTC, "currency")}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{invoice.pourcentage !== undefined && invoice.pourcentage !== null ? `${invoice.pourcentage}%` : "-"}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{invoice.montantFactureHT !== undefined && invoice.montantFactureHT !== null ? formatValue(invoice.montantFactureHT, "currency") : "-"}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{invoice.montantFactureTTC !== undefined && invoice.montantFactureTTC !== null ? formatValue(invoice.montantFactureTTC, "currency") : "-"}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{formatValue(invoice.date, "date")}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        fontWeight: '500',
                        backgroundColor: invoice.status === 'Réglée' ? '#d4edda' : invoice.status === 'Gelée' ? '#cfe2ff' : '#f8d7da',
                        color: invoice.status === 'Réglée' ? '#155724' : invoice.status === 'Gelée' ? '#084298' : '#721c24',
                        border: `1px solid ${invoice.status === 'Réglée' ? '#c3e6cb' : invoice.status === 'Gelée' ? '#b6d4fe' : '#f5c6cb'}`
                      }}>
                        {invoice.status === 'Pas Réglée' ? 'En attente de Paiement' : (invoice.status || 'En attente de Paiement')}
                      </span>
                    </td>
                    <td className="document-icon-cell" style={{ fontFamily: 'Inter, sans-serif' }}>
                      <i
                        className="bi bi-filetype-pdf"
                        title="Aperçu PDF"
                        onClick={() => handleGenerateInvoicePdf(invoice)}
                        style={{ cursor: 'pointer', marginRight: '10px', fontSize: '1.2rem', color: '#0d6efd' }}
                      />
                    </td>
                    <td className="action-icons">
                      <i className="bi bi-pencil-square edit-icon" title="Modifier" onClick={() => handleEditInvoice(invoice)} style={{ color: '#007bff', cursor: 'pointer' }}></i>
                      <i className="bi bi-files copy-icon" title="Dupliquer" onClick={() => handleDuplicateInvoice(invoice)} style={{ color: '#28a745', cursor: 'pointer', marginLeft: 2 }}></i>
                      <i className="bi bi-trash delete-icon" title="Supprimer" onClick={() => handleDeleteInvoice(invoice)} style={{ color: '#dc3545', cursor: 'pointer', marginLeft: 2 }}></i>
                    </td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}>Total</td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}></td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}></td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}></td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}></td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}></td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}></td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}></td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}>
                    {formatValue(totals.totalFactureHT, "currency")}
                  </td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}>
                    {formatValue(totals.totalFactureTTC, "currency")}
                  </td>
                  <td colSpan={4}></td>
                </tr>
              </>
            )}
          </tbody>
        </table>
        
        {/* Totaux par statut */}
        <div style={{ marginTop: '20px', display: 'flex', gap: '20px', justifyContent: 'space-around', fontFamily: 'Inter, sans-serif' }}>
          {/* Total Réglées */}
          {(selectedStatus === 'all' || selectedStatus === 'Réglée') && (
          <div style={{
            padding: '20px',
            backgroundColor: '#d4edda',
            borderRadius: '5px',
            border: '1px solid #c3e6cb',
            flex: 1,
            minWidth: '500px',
            maxWidth: '600px'
          }}>
            <h4 style={{ margin: '0 0 15px 0', color: '#155724', fontWeight: '600', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="bi bi-bank"></i>
              Total Réglées
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '1rem' }}>
              <div><strong>Facturé HT:</strong> {formatValue(totalsByStatus.reglee.totalHT, "currency")}</div>
              <div><strong>Facturé TTC:</strong> {formatValue(totalsByStatus.reglee.totalTTC, "currency")}</div>
            </div>
          </div>
          )}

          {/* Total En attente de Paiement */}
          {(selectedStatus === 'all' || selectedStatus === 'En attente de Paiement') && (
          <div style={{
            padding: '20px',
            backgroundColor: '#f8d7da',
            borderRadius: '5px',
            border: '1px solid #f5c6cb',
            flex: 1,
            minWidth: '500px',
            maxWidth: '600px'
          }}>
            <h4 style={{ margin: '0 0 15px 0', color: '#721c24', fontWeight: '600', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="bi bi-wallet2"></i>
              Total En attente de Paiement
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '1rem' }}>
              <div><strong>Facturé HT:</strong> {formatValue(totalsByStatus.enAttente.totalHT, "currency")}</div>
              <div><strong>Facturé TTC:</strong> {formatValue(totalsByStatus.enAttente.totalTTC, "currency")}</div>
            </div>
          </div>
          )}

          {/* Total Gelées */}
          {(selectedStatus === 'all' || selectedStatus === 'Gelée') && (
          <div style={{
            padding: '20px',
            backgroundColor: '#cfe2ff',
            borderRadius: '5px',
            border: '1px solid #b6d4fe',
            flex: 1,
            minWidth: '500px',
            maxWidth: '600px'
          }}>
            <h4 style={{ margin: '0 0 15px 0', color: '#084298', fontWeight: '600', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="bi bi-snow"></i>
              Total Gelées
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '1rem' }}>
              <div><strong>Facturé HT:</strong> {formatValue(totalsByStatus.gelee.totalHT, "currency")}</div>
              <div><strong>Facturé TTC:</strong> {formatValue(totalsByStatus.gelee.totalTTC, "currency")}</div>
            </div>
          </div>
          )}
        </div>
      </div>

      <FactureModal
        show={showModal}
        handleClose={handleCloseModal}
        initialData={invoiceToEdit}
        preselectedDevis={preselectedDevis}
      />

      <FacturePreviewModal
        show={showPreviewModal}
        handleClose={handleClosePreviewModal}
        invoiceData={invoiceToPreview}
        pdfData={invoicePdfPreviewUrl}
      />

      {invoiceToDelete && (
        <div className="modal-overlay">
          <div className="delete-confirmation">
            <h2>Confirmer la suppression</h2>
            <p>Êtes-vous sûr de vouloir supprimer la facture "{invoiceToDelete.id}" de la société "{invoiceToDelete.companyId}" ?</p>
            <p className="warning-text">Cette action est irréversible.</p>
            <div className="modal-actions">
              <button className="cancel-button" onClick={cancelDeleteInvoice}>
                Annuler
              </button>
              <button className="delete-button" onClick={confirmDeleteInvoice}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      <BillingRecapTable />

      <Footer />
    </div>
  );
};

export default Factures; 